import { useRouter } from 'next/router';
import { useState, useTransition } from 'react';

import { Box, ToastProvider, useToast } from '@vimeo/bokeh';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { translate } from '@vimeo/i18n-runtime';
import { ShowcaseNavigation } from '@vimeo/viewer-showcase-ui/components/ShowcaseNavigation';

import { useGetHeaderLayoutConfig } from '../hooks/use-get-header-layout-config';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useSelectedId } from '../hooks/use-selected-id';
import { useShowNewCustomizeControls } from '../hooks/use-show-new-customize-controls';
import { useShowcaseSearch } from '../hooks/use-showcase-search';
import { useAlbumStore } from '../state/albumStore';
import { usePathname } from '../utils/helper';

export const ShowcaseNavigationModule = ({ isShowcaseEmpty }: { isShowcaseEmpty: boolean }) => {
  const [, startTransition] = useTransition();
  const isNewCustomizeControls = useShowNewCustomizeControls();

  const router = useRouter();
  const pathname = usePathname();
  const { onClose } = useLayout();
  const { selectedId } = useSelectedId();
  const toast = useToast();

  const {
    customLogo,
    allowShare,
    title,
    canEditShowcase,
    hideNav,
    shareLink,
    showVideoDetails,
    showBannerTitle,
    showSearchBar,
  } = useGetShowcase();
  const albumId = useAlbumStore((state) => state.albumId);

  const [searchedTerm, setSearchedTerm] = useState('');
  const { isLoading, results, canLoadMore, error, loadMoreResults } =
    useShowcaseSearch(searchedTerm);
  const handleSearch = (text: string) => {
    setSearchedTerm(text);
  };
  const handleResetSearch = () => setSearchedTerm('');
  const handleResultClick = () => handleResetSearch();

  const layoutConfig = useGetHeaderLayoutConfig({ isShowcaseEmpty, showVideoDetails });

  const onShareClick = () => {
    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(shareLink);
        toast({ title: translate('Link copied') });
      } catch (error) {
        toast({ title: translate('Oops, something went wrong. Please try again.') });
      }
    };

    copyUrl();
  };

  const navigateToShowcaseHome = () => {
    startTransition(() => {
      router.push(pathname);
      onClose?.();
    });
  };

  const navigateToManageShowcase = () => {
    window.location.href = `/manage/showcases/${albumId}`;
  };

  if (layoutConfig === null) {
    return null; // This renders a loading state
  }

  return (
    <ToastProvider>
      <Box
        position="relative"
        {...(!hideNav ? { borderTop: '1px solid', borderColor: 'stroke' } : {})}
      >
        <ShowcaseNavigation
          allowShare={allowShare}
          canLoadMore={canLoadMore}
          customLogo={customLogo}
          isNewCustomizeControls={isNewCustomizeControls}
          isSearchVisible={showSearchBar}
          isShowcaseEmpty={isShowcaseEmpty}
          resultsPending={isLoading}
          resultsList={results}
          searchError={error}
          searchedTerm={searchedTerm}
          showBackButton={!!selectedId}
          showManageButton={canEditShowcase}
          textColor={layoutConfig.thumbnailUrl && !selectedId ? 'white' : 'text-primary'}
          topTitle={showBannerTitle ? (!!selectedId ? title : layoutConfig.topTitle) : ''} // Always show top title when in clip view
          onBackClick={navigateToShowcaseHome}
          onLoadMoreSearchResults={loadMoreResults}
          onManageClick={navigateToManageShowcase}
          onResetSearch={handleResetSearch}
          onResultClick={handleResultClick}
          onSearch={handleSearch}
          onShareClick={onShareClick}
          showcaseId={albumId}
          {...(!selectedId && { position: 'absolute', top: 0, left: 0, zIndex: 3, w: '100%' })}
        />
      </Box>
    </ToastProvider>
  );
};
